import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../../../store'
import {
  StateFieldsResponse,
  SessionWebhooksResponse,
  PayloadManagementState,
  StateField,
  StateCollection,
  FieldType,
  CreateStateCollectionRequest,
  UpdateStateCollectionRequest,
  CreateSessionWebhookRequest,
  UpdateSessionWebhookRequest,
  SessionWebhook,
  SessionWebhookPayload,
  SessionWebhookRegistry,
  SessionSMSHook
} from '../../../interfaces/payloads'
import { getServicesManager } from '../../../services'


// Async thunks for session webhooks
export const fetchSessionWebhooks = createAsyncThunk(
  'payloads/fetchSessionWebhooks',
  async (_, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.getSessionWebhooks()
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch session webhooks')
    }
  }
)

// Async thunks for state fields
export const fetchStateFields = createAsyncThunk(
  'payloads/fetchStateFields',
  async (_, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.getStateFields()
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch state fields')
    }
  }
)

export const createStateCollection = createAsyncThunk(
  'payloads/createStateCollection',
  async (data: CreateStateCollectionRequest, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.createStateCollection(data)
      
      // Check if result is an error from the backend
      if (typeof result === 'string') {
        // This is an error message from a 400 response
        return rejectWithValue(result)
      }
      
      if (result === null) {
        return rejectWithValue('Failed to create state collection - please check your input and try again')
      }
      
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create state collection')
    }
  }
)

export const updateStateCollection = createAsyncThunk(
  'payloads/updateStateCollection',
  async ({ stateId, data }: { stateId: string, data: UpdateStateCollectionRequest }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.updateStateCollection(stateId, data)
      
      // Check if result is an error from the backend
      if (typeof result === 'string') {
        // This is an error message from a 400 response
        return rejectWithValue(result)
      }
      
      if (result === null) {
        return rejectWithValue('Failed to update state collection - please check your input and try again')
      }
      
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update state collection')
    }
  }
)

export const deleteStateCollection = createAsyncThunk(
  'payloads/deleteStateCollection',
  async (stateId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteStateCollection(stateId)
      return stateId
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete state collection')
    }
  }
)


// Bulk update all state fields for a collection
export const bulkUpdateStateFields = createAsyncThunk(
  'payloads/bulkUpdateStateFields',
  async (data: { stateId: string, fields: StateField[], collectionName: string, isNewCollection?: boolean }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      console.log(`Bulk updating ${data.fields.length} fields for state: ${data.stateId}`, data.fields)
      
      const result = await servicesManager.bulkUpdateStateFields(data.stateId, data.fields, data.collectionName, data.isNewCollection)
      
      // Check if the service call was successful
      if (!result.success) {
        console.error('❌ Bulk update service returned error:', result.error_message)
        return rejectWithValue(result.error_message || 'Failed to update state fields')
      }
      
      console.log('✅ Bulk update successful in thunk')
      return { stateId: data.stateId, fields: data.fields }
    } catch (error) {
      console.error('❌ Redux bulkUpdateStateFields thunk caught error:', error)
      
      // Extract the actual error message from the caught error
      let errorMessage = 'Failed to update state fields'
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      console.log('⚠️ Rejecting with error message:', errorMessage)
      return rejectWithValue(errorMessage)
    }
  }
)


export const createSessionWebhook = createAsyncThunk(
  'payloads/createSessionWebhook',
  async (data: CreateSessionWebhookRequest, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.createSessionWebhook(data)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create session webhook')
    }
  }
)

export const updateSessionWebhook = createAsyncThunk(
  'payloads/updateSessionWebhook',
  async ({ webhookId, data }: { webhookId: string, data: UpdateSessionWebhookRequest }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.updateSessionWebhook(webhookId, data)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update session webhook')
    }
  }
)

export const deleteSessionWebhook = createAsyncThunk(
  'payloads/deleteSessionWebhook',
  async (webhookId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteSessionWebhook(webhookId)
      return webhookId
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete session webhook')
    }
  }
)

export const createWebhookPayload = createAsyncThunk(
  'payloads/createWebhookPayload',
  async (data: { webhookId: string, payload: Partial<SessionWebhookPayload> }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.createWebhookPayload(data.webhookId, data.payload as SessionWebhookPayload)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create webhook payload')
    }
  }
)

export const updateWebhookPayload = createAsyncThunk(
  'payloads/updateWebhookPayload',
  async (data: { webhookId: string, payloadKey: string, payload: Partial<SessionWebhookPayload> }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.updateWebhookPayload(data.webhookId, data.payloadKey, data.payload as SessionWebhookPayload)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update webhook payload')
    }
  }
)

export const deleteWebhookPayload = createAsyncThunk(
  'payloads/deleteWebhookPayload',
  async (data: { webhookId: string, payloadKey: string }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteWebhookPayload(data.webhookId, data.payloadKey)
      return data
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete webhook payload')
    }
  }
)

export const createWebhookRegistry = createAsyncThunk(
  'payloads/createWebhookRegistry',
  async (data: { webhookId: string, registry: Partial<SessionWebhookRegistry> }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.createWebhookRegistry(data.webhookId, data.registry as SessionWebhookRegistry)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create webhook registry')
    }
  }
)

export const updateWebhookRegistry = createAsyncThunk(
  'payloads/updateWebhookRegistry',
  async (data: { webhookId: string, registryId: string, registry: Partial<SessionWebhookRegistry> }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.updateWebhookRegistry(data.webhookId, data.registryId, data.registry)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update webhook registry')
    }
  }
)

export const deleteWebhookRegistry = createAsyncThunk(
  'payloads/deleteWebhookRegistry',
  async (data: { webhookId: string, registryId: string }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteWebhookRegistry(data.webhookId, data.registryId)
      return data
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete webhook registry')
    }
  }
)

// SMS Hook CRUD operations
export const createSMSHook = createAsyncThunk(
  'payloads/createSMSHook',
  async (data: Partial<SessionSMSHook>, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.createSMSHook(data)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create SMS hook')
    }
  }
)

export const updateSMSHook = createAsyncThunk(
  'payloads/updateSMSHook',
  async ({ hookId, data }: { hookId: string, data: Partial<SessionSMSHook> }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const response = await servicesManager.updateSMSHook(hookId, data)
      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update SMS hook')
    }
  }
)

export const deleteSMSHook = createAsyncThunk(
  'payloads/deleteSMSHook',
  async (hookId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteSMSHook(hookId)
      return hookId
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete SMS hook')
    }
  }
)

const initialState: PayloadManagementState = {
  sessionWebhooks: {
    webhooks: [],
    sms_hooks: [],
    registries: [],
    payloads: [],
    isLoading: false,
    error: null
  },
  stateFields: {
    fields: [],
    collections: [],
    isLoading: false,
    error: null
  }
}

const payloadManagementSlice = createSlice({
  name: 'payloadManagement',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.sessionWebhooks.error = null
      state.stateFields.error = null
    }
  },
  extraReducers: (builder) => {
    // Session Webhooks
    builder
      .addCase(fetchSessionWebhooks.pending, (state) => {
        state.sessionWebhooks.isLoading = true
        state.sessionWebhooks.error = null
      })
      .addCase(fetchSessionWebhooks.fulfilled, (state, action) => {
        state.sessionWebhooks.isLoading = false
        const payload = action.payload as SessionWebhooksResponse
        state.sessionWebhooks.webhooks = payload?.webhooks || []
        state.sessionWebhooks.sms_hooks = payload?.sms_hooks || []
        state.sessionWebhooks.registries = payload?.registries || []
        state.sessionWebhooks.payloads = payload?.payloads || []
      })
      .addCase(fetchSessionWebhooks.rejected, (state, action) => {
        state.sessionWebhooks.isLoading = false
        state.sessionWebhooks.error = action.payload as string
      })

    // State Fields
    builder
      .addCase(fetchStateFields.pending, (state) => {
        state.stateFields.isLoading = true
        state.stateFields.error = null
      })
      .addCase(fetchStateFields.fulfilled, (state, action) => {
        state.stateFields.isLoading = false
        const payload = action.payload as StateFieldsResponse
        state.stateFields.fields = payload?.state_fields || []
        state.stateFields.collections = payload?.state_collections || []
      })
      .addCase(fetchStateFields.rejected, (state, action) => {
        state.stateFields.isLoading = false
        state.stateFields.error = action.payload as string
      })

    // State Collection CRUD
    builder
      .addCase(createStateCollection.pending, (state) => {
        state.stateFields.error = null
      })
      .addCase(createStateCollection.fulfilled, (state, action) => {
        if (action.payload) {
          const result = action.payload as any

          // Add the collection
          const collection: StateCollection = {
            state_id: result.state_id,
            team_id: result.team_id,
            name: result.name
          }
          state.stateFields.collections.push(collection)

          // Add the fields if they were returned
          if (result.state_fields && Array.isArray(result.state_fields)) {
            state.stateFields.fields.push(...result.state_fields)
          }
        }
      })
      .addCase(createStateCollection.rejected, (state, action) => {
        state.stateFields.error = action.payload as string
      })
      .addCase(updateStateCollection.pending, (state) => {
        state.stateFields.error = null
      })
      .addCase(updateStateCollection.fulfilled, (state, action) => {
        // Update the collection in the local state
        const updatedCollection = action.payload as StateCollection
        const collectionIndex = state.stateFields.collections.findIndex(
          c => c.state_id === updatedCollection.state_id
        )
        if (collectionIndex !== -1) {
          state.stateFields.collections[collectionIndex] = updatedCollection
        }
      })
      .addCase(updateStateCollection.rejected, (state, action) => {
        state.stateFields.error = action.payload as string
      })
      .addCase(deleteStateCollection.pending, (state) => {
        state.stateFields.error = null
      })
      .addCase(deleteStateCollection.fulfilled, (state, action) => {
        const deletedStateId = action.payload as string
        state.stateFields.collections = state.stateFields.collections.filter(
          collection => collection.state_id !== deletedStateId
        )
        state.stateFields.fields = state.stateFields.fields.filter(
          field => field.state_id !== deletedStateId
        )
      })
      .addCase(deleteStateCollection.rejected, (state, action) => {
        state.stateFields.error = action.payload as string
      })


    // Bulk State Field Update
    builder
      .addCase(bulkUpdateStateFields.pending, (state) => {
        state.stateFields.error = null
      })
      .addCase(bulkUpdateStateFields.fulfilled, (state, action) => {
        const { stateId, fields } = action.payload
        // Replace all fields for this state collection with the new ones
        state.stateFields.fields = state.stateFields.fields.filter(
          field => field.state_id !== stateId
        ).concat(fields)
      })
      .addCase(bulkUpdateStateFields.rejected, (state, action) => {
        state.stateFields.error = action.payload as string
      })

    // Session Webhook CRUD
    builder
      .addCase(createSessionWebhook.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(createSessionWebhook.fulfilled, (state, action) => {
        if (action.payload) {
          const result = action.payload as any

          // Add the webhook
          const webhook: SessionWebhook = {
            hook_id: result.hook_id || result.webhook_id,
            webhook_id: result.webhook_id || result.hook_id,
            team_id: result.team_id,
            url: result.url,
            http_verb: result.http_verb,
            encoding: result.encoding,
            notify_not_ok: result.notify_not_ok
          }
          state.sessionWebhooks.webhooks.push(webhook)

          // Add the payloads if they were returned
          if (result.payloads && Array.isArray(result.payloads)) {
            state.sessionWebhooks.payloads.push(...result.payloads)
          }

          // Add the registries if they were returned
          if (result.registries && Array.isArray(result.registries)) {
            state.sessionWebhooks.registries.push(...result.registries)
          }
        }
      })
      .addCase(createSessionWebhook.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(updateSessionWebhook.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(updateSessionWebhook.fulfilled, (state, action) => {
        // Update the webhook in the store based on the request data
        const { webhookId, data } = action.meta.arg
        const index = state.sessionWebhooks.webhooks.findIndex(
          webhook => webhook.webhook_id === webhookId
        )
        if (index !== -1) {
          // Update webhook properties
          if (data.url) state.sessionWebhooks.webhooks[index].url = data.url
          if (data.http_verb) state.sessionWebhooks.webhooks[index].http_verb = data.http_verb as any
          if (data.encoding !== undefined) state.sessionWebhooks.webhooks[index].encoding = data.encoding as any
          if (data.notify_not_ok !== undefined) state.sessionWebhooks.webhooks[index].notify_not_ok = data.notify_not_ok
        }
        
        // Update registries if provided
        if (data.registries) {
          // Remove existing registries for this webhook
          state.sessionWebhooks.registries = state.sessionWebhooks.registries.filter(
            registry => registry.webhook_id !== webhookId
          )
          // Add updated registries
          state.sessionWebhooks.registries.push(...data.registries)
        }
        
        // Update payloads if provided
        if (data.payloads) {
          // Remove existing payloads for this webhook
          state.sessionWebhooks.payloads = state.sessionWebhooks.payloads.filter(
            payload => payload.webhook_id !== webhookId
          )
          // Add updated payloads
          state.sessionWebhooks.payloads.push(...data.payloads)
        }
      })
      .addCase(updateSessionWebhook.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(deleteSessionWebhook.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(deleteSessionWebhook.fulfilled, (state, action) => {
        const deletedWebhookId = action.payload as string
        state.sessionWebhooks.webhooks = state.sessionWebhooks.webhooks.filter(
          webhook => webhook.webhook_id !== deletedWebhookId
        )
        state.sessionWebhooks.registries = state.sessionWebhooks.registries.filter(
          registry => registry.webhook_id !== deletedWebhookId
        )
        state.sessionWebhooks.payloads = state.sessionWebhooks.payloads.filter(
          payload => payload.webhook_id !== deletedWebhookId
        )
      })
      .addCase(deleteSessionWebhook.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })

    // Webhook Payload CRUD
    builder
      .addCase(createWebhookPayload.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(createWebhookPayload.fulfilled, (state, action) => {
        if (action.payload) {
          state.sessionWebhooks.payloads.push(action.payload)
        }
      })
      .addCase(createWebhookPayload.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(updateWebhookPayload.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(updateWebhookPayload.fulfilled, (state, action) => {
        if (action.payload) {
          const { webhookId, payloadKey } = action.meta.arg
          const index = state.sessionWebhooks.payloads.findIndex(
            payload => payload.webhook_id === webhookId && payload.key === payloadKey
          )
          if (index !== -1) {
            state.sessionWebhooks.payloads[index] = action.payload
          }
        }
      })
      .addCase(updateWebhookPayload.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(deleteWebhookPayload.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(deleteWebhookPayload.fulfilled, (state, action) => {
        const { webhookId, payloadKey } = action.payload
        state.sessionWebhooks.payloads = state.sessionWebhooks.payloads.filter(
          payload => !(payload.webhook_id === webhookId && payload.key === payloadKey)
        )
      })
      .addCase(deleteWebhookPayload.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })

    // Webhook Registry CRUD
    builder
      .addCase(createWebhookRegistry.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(createWebhookRegistry.fulfilled, (state, action) => {
        if (action.payload) {
          state.sessionWebhooks.registries.push(action.payload)
        }
      })
      .addCase(createWebhookRegistry.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(updateWebhookRegistry.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(updateWebhookRegistry.fulfilled, (state, action) => {
        const { registryId, registry } = action.meta.arg
        const index = state.sessionWebhooks.registries.findIndex(
          reg => reg.registry_id === registryId
        )

        if (index !== -1) {
          // Backend returns null on success, so update using our sent data
          if (action.payload) {
            // If backend returns data, use it
            state.sessionWebhooks.registries[index] = action.payload
          } else {
            // Otherwise, merge our updates with the existing registry
            const existingRegistry = state.sessionWebhooks.registries[index]
            state.sessionWebhooks.registries[index] = {
              ...existingRegistry,
              all_sessions: registry.all_sessions !== undefined ? registry.all_sessions : existingRegistry.all_sessions,
              route_id: registry.route_id !== undefined ? registry.route_id : existingRegistry.route_id,
              agent_id: registry.agent_id !== undefined ? registry.agent_id : existingRegistry.agent_id,
              event: registry.event || existingRegistry.event,
              requirement_rule: registry.requirement_rule !== undefined ? registry.requirement_rule : existingRegistry.requirement_rule
            }
          }
        }
      })
      .addCase(updateWebhookRegistry.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(deleteWebhookRegistry.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(deleteWebhookRegistry.fulfilled, (state, action) => {
        const { registryId } = action.payload
        state.sessionWebhooks.registries = state.sessionWebhooks.registries.filter(
          registry => registry.registry_id !== registryId
        )
      })
      .addCase(deleteWebhookRegistry.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })

    // SMS Hook CRUD
    builder
      .addCase(createSMSHook.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(createSMSHook.fulfilled, (state, action) => {
        if (action.payload) {
          state.sessionWebhooks.sms_hooks.push(action.payload)
        }
      })
      .addCase(createSMSHook.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(updateSMSHook.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(updateSMSHook.fulfilled, (state, action) => {
        if (action.payload) {
          const { hookId } = action.meta.arg
          const index = state.sessionWebhooks.sms_hooks.findIndex(
            hook => hook.hook_id === hookId
          )
          if (index !== -1) {
            state.sessionWebhooks.sms_hooks[index] = action.payload
          }
        }
      })
      .addCase(updateSMSHook.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
      .addCase(deleteSMSHook.pending, (state) => {
        state.sessionWebhooks.error = null
      })
      .addCase(deleteSMSHook.fulfilled, (state, action) => {
        const deletedHookId = action.payload as string
        state.sessionWebhooks.sms_hooks = state.sessionWebhooks.sms_hooks.filter(
          hook => hook.hook_id !== deletedHookId
        )
        // Also remove related registries and payloads
        state.sessionWebhooks.registries = state.sessionWebhooks.registries.filter(
          registry => registry.hook_id !== deletedHookId
        )
        state.sessionWebhooks.payloads = state.sessionWebhooks.payloads.filter(
          payload => payload.hook_id !== deletedHookId
        )
      })
      .addCase(deleteSMSHook.rejected, (state, action) => {
        state.sessionWebhooks.error = action.payload as string
      })
  }
})

export const { clearErrors } = payloadManagementSlice.actions
export default payloadManagementSlice.reducer